// Dependencies
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useIntl } from "react-intl";
import clsx from "clsx";
import { isEmpty } from "lodash-es";

// Redux dependencies
import { useDispatch } from "react-redux";
import { addSnackbar } from "../../../redux/snackbarSlice";

// Components
import NavigationButtons from "./NavigationButtons";

import makeStyles from "@mui/styles/makeStyles";
import {
  Box,
  Divider,
  TextField,
  Typography,
  Button,
  MenuItem,
  Select,
  FormControl,
  InputLabel
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import HandleTextModal from "../../library/HandleTextModal";
import { useFeatureFlags } from "../../../hooks/useFeatureFlags";
import {
  FEATURE_FLAGS,
  TASK,
  TASK_TYPES,
  TASK_TYPES_NEW
} from "../../../consts";
import { setTaskCreationStates } from "../../../redux/tasksSlice";
import { RichTooltipPopover } from "../../SharedComponents/RichTooltipPopover";

//Styles
const useStyles = makeStyles(() => ({
  section: {
    marginBottom: "50px"
  },
  selectText: {
    width: "80%"
  },
  divider: {
    backgroundColor: "#dedede",
    marginBottom: "8px",
    marginTop: "8px"
  },
  text: {
    marginTop: "32px",
    marginBottom: "30px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center"
  },
  assignmentType: {
    marginTop: "24px",
    width: "100%"
  },
  popover: {
    left: "20px"
  },
  coach: {
    display: "flex",
    justifyContent: "flex-start"
  }
}));

function CreateAssignmentReadings({
  texts,
  text,
  setTextId,
  activeStep,
  setActiveStep,
  publishTask,
  saveTask,
  setIsDirty,
  readOnly = false,
  resetQuestions = null,
  taskType,
  setTaskType,
  botMode,
  setBotMode
}) {
  //Hooks
  const intl = useIntl();
  const classes = useStyles();
  const dispatch = useDispatch();
  const newAssignmentLayoutEnabled = useFeatureFlags(
    FEATURE_FLAGS.NEW_ASSIGNMENT_LAYOUT
  );

  // Variables
  const validationMessage = intl.formatMessage({
    id: "tasks.create.validation.selectReading",
    defaultMessage: "Select a text"
  });
  const [textDisplay, setTextDisplay] = React.useState("");
  const [openTexts, setOpenTexts] = useState(false);
  const [uploadedText, setUploadedText] = useState(null);
  const [showErrors, setShowErrors] = useState(false);

  const taskTypes = newAssignmentLayoutEnabled ? TASK_TYPES_NEW : TASK_TYPES;

  //Behavior
  function validateForm() {
    if (!text.id) {
      setShowErrors(true);
      dispatch(
        addSnackbar({
          message: validationMessage,
          severity: "error"
        })
      );
      return false;
    } else return true;
  }

  useEffect(() => {
    if (uploadedText && texts.find((t) => t.id === uploadedText.id)) {
      setTextId(uploadedText.id);
      setUploadedText(null);
    }
  }, [texts, uploadedText]);

  const renderAssignmentTypeTooltipBody = () => {
    return newAssignmentLayoutEnabled ? (
      <Box>
        <Typography variant="subtitle1">
          {intl.formatMessage({
            id: "task.type.journey",
            defaultMessage: "Guided Journey"
          })}
        </Typography>
        <Typography
          variant="body2"
          sx={{ marginBottom: "10px", fontWeight: "300" }}>
          {intl.formatMessage({
            id: "task.create.readings.newLayout.assignmentTypeTooltipJourney",
            defaultMessage: "Standard"
          })}
        </Typography>
        <Typography variant="subtitle1">
          {intl.formatMessage({
            id: "task.type.standardNew",
            defaultMessage: "Interactive Analysis"
          })}
        </Typography>
        <Typography
          variant="body2"
          sx={{ marginBottom: "10px", fontWeight: "300" }}>
          {intl.formatMessage({
            id: "task.create.readings.newLayout.assignmentTypeTooltipStandard",
            defaultMessage: "Standard"
          })}
        </Typography>
        <Typography variant="subtitle1">
          {intl.formatMessage({
            id: "task.type.guidedReadingNew",
            defaultMessage: "Self Directed Questions"
          })}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: "300" }}>
          {intl.formatMessage({
            id: "task.create.readings.newLayout.assignmentTypeTooltipGuidedReading",
            defaultMessage: "Self Directed Questions"
          })}
        </Typography>
      </Box>
    ) : (
      <Box>
        <Typography variant="subtitle1">
          {intl.formatMessage({
            id: "task.type.standard",
            defaultMessage: "Standard"
          })}
        </Typography>
        <Typography variant="body2" sx={{ marginBottom: "10px" }}>
          {intl.formatMessage({
            id: "task.create.readings.assignmentTypeTooltipStandard",
            defaultMessage: "Standard"
          })}
        </Typography>
        <Typography variant="subtitle1">
          {intl.formatMessage({
            id: "task.type.guidedReading",
            defaultMessage: "Guided reading"
          })}
        </Typography>
        <Typography variant="body2">
          {intl.formatMessage({
            id: "task.create.readings.assignmentTypeTooltipGuidedReading",
            defaultMessage: "Guided reading"
          })}
        </Typography>
      </Box>
    );
  };

  //Render
  return (
    <Box data-testid="create-assignment-readings">
      <Box className={classes.section}>
        <Typography variant="h6">
          {intl.formatMessage({
            id: "task.create.readings.readingMaterial",
            defaultMessage: "Reading material"
          })}
        </Typography>
        <Divider className={classes.divider} />
        <Box className={classes.text}>
          <Autocomplete
            className={classes.selectText}
            disabled={readOnly}
            debug="true"
            id="selectReading"
            options={texts}
            getOptionKey={(text) => text.id}
            getOptionLabel={(option) => {
              return option ? option.author + " - " + option.name : "";
            }}
            renderInput={(params) => (
              <TextField
                readOnly={readOnly}
                {...params}
                name="select-text"
                aria-label={intl.formatMessage({
                  id: "task.create.readings.selectText",
                  defaultMessage: "Type the title or author name"
                })}
                label={intl.formatMessage({
                  id: "task.create.readings.selectText",
                  defaultMessage: "Type the title or author name"
                })}
                data-testid="select-text-input"
                error={showErrors && !text.id}
                onBlur={() => setShowErrors(false)}
                sx={{
                  "& .MuiAutocomplete-endAdornment .MuiSvgIcon-root": {
                    color: showErrors && !text.id ? "error.main" : "inherit"
                  }
                }}
              />
            )}
            value={isEmpty(text) ? null : text}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            onChange={(event, newValue) => {
              resetQuestions && resetQuestions();
              if (newValue && newValue.file_url) {
                setTextId(newValue.id);
                setIsDirty(true);
              }
            }}
            inputValue={textDisplay}
            onInputChange={(event, newInputValue) => {
              setTextDisplay(newInputValue);
            }}
          />
          <Typography variant="h6">
            {intl.formatMessage({
              id: "task.create.readings.orUpload",
              defaultMessage: "Or"
            })}
          </Typography>
          <Button
            variant="outlined"
            size="large"
            onClick={() => setOpenTexts("Add")}
            disabled={readOnly}
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                setOpenTexts("Add");
              }
            }}>
            {intl.formatMessage({
              id: "task.create.readings.upload",
              defaultMessage: "Upload"
            })}
          </Button>
          {openTexts && (
            <HandleTextModal
              openTexts={openTexts}
              setOpenTexts={setOpenTexts}
              setUploadedText={setUploadedText}
            />
          )}
        </Box>
      </Box>
      <Box className={classes.section}>
        <Box sx={{ display: "flex", alignItems: "center", maxHeight: "32px" }}>
          <Typography className={clsx(classes.paddedContent)} variant="h6">
            {intl.formatMessage({
              id: "task.create.readings.assignmentType",
              defaultMessage: "Assignment type"
            })}
          </Typography>
          <RichTooltipPopover
            id="assisgnment-type-tooltip-popover"
            name="Assignment Type Tooltip"
            readOnly={readOnly}
            style={{ width: "600px" }}
            title="task.create.readings.assignmentType"
            location="right"
            bodyHtml={renderAssignmentTypeTooltipBody()}
          />
        </Box>
        <Divider className={classes.divider} />
        <FormControl className={classes.assignmentType}>
          <InputLabel id="taskType-label">
            {intl.formatMessage({
              id: "task.create.readings.assignmentType",
              defaultMessage: "Assignment type"
            })}
          </InputLabel>
          <Select
            labelId="taskType-label"
            sx={{ width: "100%" }}
            variant="outlined"
            id={`taskType`}
            aria-label="task-subtitle-input"
            aria-controls="taskType"
            disabled={readOnly}
            label={intl.formatMessage({
              id: "task.create.readings.assignmentType",
              defaultMessage: "Assignment type"
            })}
            value={taskType}
            onChange={(event) => {
              setTaskType(event.target.value);
              setIsDirty(true);
            }}
            data-testid="select-asignment-type">
            {taskTypes.map((item) => {
              return (
                <MenuItem
                  className={classes.menu}
                  value={item.key}
                  key={item.key}>
                  {intl.formatMessage({
                    id: item.value,
                    defaultMessage: "Task"
                  })}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
      </Box>
      <Box sx={{ alignItems: "center" }}>
        <Box className={classes.section}>
          <Typography variant="h6" component="h2">
            {intl.formatMessage({
              id: "task.create.readings.coachQuestion",
              defaultMessage: "AI Academic Coach Settings"
            })}
          </Typography>
          <Divider className={classes.divider} />
          <Box
            className={clsx(classes.coach, classes.sectionContent)}
            sx={{ display: "flex", alignItems: "center", marginTop: "24px" }}>
            <FormControl sx={{ width: "350px" }}>
              <InputLabel id="taskType-label">
                {intl.formatMessage({
                  id: "task.create.readings.academicCoachMode",
                  defaultMessage: "AI academic coach mode"
                })}
              </InputLabel>
              <Select
                labelId="taskType-label"
                variant="outlined"
                id={`taskType`}
                aria-label="AI Academic coach mode"
                disabled={readOnly || taskType === TASK.TYPE.JOURNEY}
                label={intl.formatMessage({
                  id: "task.create.readings.academicCoachMode",
                  defaultMessage: "AI Academic coach mode"
                })}
                value={botMode}
                onChange={(event) => {
                  setBotMode(event.target.value);
                  dispatch(
                    setTaskCreationStates(
                      "taskCreationBotMode",
                      event.target.value
                    )
                  );
                  setIsDirty(true);
                }}
                data-testid="select-bot-mode">
                <MenuItem value="disabled">
                  {intl.formatMessage({
                    id: "task.create.readings.academicCoachDisable",
                    defaultMessage: "Disable"
                  })}
                </MenuItem>
                <MenuItem value="full">
                  {intl.formatMessage({
                    id: "task.create.readings.academicCoachFull",
                    defaultMessage: "Enable with discussion-based answers"
                  })}
                </MenuItem>
                <MenuItem value="chat">
                  {intl.formatMessage({
                    id: "task.create.readings.academicCoachChat",
                    defaultMessage: "Enable without discussion-based answers"
                  })}
                </MenuItem>
              </Select>
            </FormControl>
            {!readOnly && (
              <RichTooltipPopover
                id="academic-coach-tooltip"
                name="AI Academic coach info"
                readOnly={readOnly}
                style={{ width: "300px" }}
                title="task.create.readings.academicCoach"
                bodyHtml={
                  <Box>
                    <Typography variant="body2">
                      {intl.formatMessage({
                        id: "task.create.readings.academicCoachTooltipCoach",
                        defaultMessage: "AI academic coach"
                      })}
                    </Typography>
                  </Box>
                }
              />
            )}
          </Box>
        </Box>
      </Box>
      {setActiveStep && (
        <NavigationButtons
          activeStep={activeStep}
          setActiveStep={setActiveStep}
          publishTask={publishTask}
          saveTask={saveTask}
          validateForm={validateForm}
        />
      )}
    </Box>
  );
}

// I added proptype sto start ceaning up eslint errors but i just put "any" - need to add correct proptypes here
CreateAssignmentReadings.propTypes = {
  texts: PropTypes.any,
  text_id: PropTypes.number,
  setTextId: PropTypes.func,
  activeStep: PropTypes.any,
  setActiveStep: PropTypes.any,
  publishTask: PropTypes.func,
  saveTask: PropTypes.func,
  setIsDirty: PropTypes.any,
  readOnly: PropTypes.any,
  resetQuestions: PropTypes.any,
  setQuestions: PropTypes.any,
  taskType: PropTypes.string,
  setTaskType: PropTypes.func,
  botMode: PropTypes.string,
  setBotMode: PropTypes.func,
  lmsSyncDisabled: PropTypes.bool,
  setLmsSyncDisabled: PropTypes.func,
  isEdit: PropTypes.bool
};

export default CreateAssignmentReadings;
